import { JSX } from "react";
import { useSignature } from "@/context/SignatureContext";
import { User } from "@/pages";

interface WelcomeMessageProps {
  user: User;
}

const MessageHeader = ({ user }: WelcomeMessageProps): JSX.Element => {
  return (
    <div>
      <div className="items-center gap-3 md:gap-5 grid grid-cols-[1fr_auto] md:grid-cols-[auto_1fr_auto] px-3 md:px-4 py-2 md:py-3 border-slate-300 border-b w-full">
        <div className="hidden md:flex items-center gap-[1px]">
          <div className="bg-slate-300 px-4 py-1.5 rounded-l-lg text-white text-base leading-[1.5]">
            Send
          </div>
          <div className="bg-slate-300 p-2 rounded-r-lg text-white">
            <img className="w-5 h-5" src="/icons/arrow-down.svg" alt="arrow-down" />
          </div>
        </div>
        <div className="mr-auto">
          <p className="text-slate-400 text-base md:text-lg truncate">
            From: {user.mail.toLowerCase()}
          </p>
        </div>
        <div className="flex items-center">
          <div className="hidden lg:flex items-center gap-5">
            <img className="w-6 h-6" src="/icons/shield.svg" alt="shield" />
            <img className="w-6 h-6" src="/icons/trash.svg" alt="trash" />
            <img className="w-6 h-6" src="/icons/new-tab-arrow.svg" alt="new-tab-arrow" />
          </div>
          <div className="lg:hidden">
            <svg
              className="w-5 h-5 text-slate-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
              />
            </svg>
          </div>
        </div>
      </div>
      <div className="px-3 md:px-4 pt-3 md:pt-4 pb-2 md:pb-3 w-full">
        <div className="flex items-end gap-2">
          <div className="px-3 md:px-4 py-1.5 border border-slate-300 rounded-l-lg text-slate-300 text-sm md:text-base leading-[1.5]">
            To
          </div>
          <div className="border-slate-300 border-b w-full"></div>
        </div>
      </div>
      <div className="px-3 md:px-4 pt-2 pb-3 md:pb-4 border-slate-300 border-b w-full">
        <div className="pb-1 border-slate-300 border-b w-full text-textDefault text-base md:text-lg">
          New Message
        </div>
      </div>
    </div>
  );
};

const WelcomeMessage = ({ user }: WelcomeMessageProps): JSX.Element => {
  const { signature } = useSignature();

  return (
    <div className="bg-white shadow-md mx-auto rounded-xl w-full h-full">
      <MessageHeader user={user} />
      <div className="p-4 max-w-7xl text-textDefault text-lg">
        <p className="">Dear {user.givenName},</p>
        <p className="mt-4">
          The form to the left of this screen allows you to update your email signature. Your email
          signature represents Care Access and its brand. Therefore, it is very important that you
          update your signature using this tool. Once done, click the{" "}
          <strong>"COPY SIGNATURE CONTENTS"</strong> button at the bottom of the sidebar on the left
          to copy your new signature. If you do not see the button, please scroll down within the
          left sidebar form (on smaller screens, it might be further down below the bottom of the
          sidebar).
        </p>
        <p className="mt-4">
          The bottom of this template is a live preview of how your signature will look after the
          update.
        </p>
        <p className="mt-4">
          For any questions, feel free to email The Care Access Marketing Team at{" "}
          <a href="mailto:marketing@careaccess.com" className="underline">
            marketing@careaccess.com
          </a>
        </p>

        {signature && (
          <div
            id="signature-preview"
            className="bg-white rounded max-w-sm"
            dangerouslySetInnerHTML={{ __html: signature }}
          />
        )}
      </div>
    </div>
  );
};
export default WelcomeMessage;
